import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useOrdenesPermisos } from '../../hooks/useOrdenesPermisos';
import { ordenesServicioAPI, ordenesClientesAPI, ordenesCatalogosAPI } from '../../lib/ordenesApi';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { ColumnSelector } from '../../components/ui/column-selector';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import {
    ClipboardList,
    Plus,
    Search,
    ArrowLeft,
    Eye,
    Clock,
    CheckCircle,
    DollarSign,
    Wrench,
    AlertCircle,
    X,
} from 'lucide-react';

const ORDEN_COLUMNS = [
    { key: 'numero', label: 'N° Orden', required: true, defaultVisible: true },
    { key: 'cliente', label: 'Cliente', defaultVisible: true },
    { key: 'rut', label: 'RUT Cliente', defaultVisible: false },
    { key: 'vehiculo', label: 'Vehículo', defaultVisible: true },
    { key: 'tipo', label: 'Tipo Servicio', defaultVisible: true },
    { key: 'estado', label: 'Estado', defaultVisible: true },
    { key: 'prioridad', label: 'Prioridad', defaultVisible: true },
    { key: 'fecha', label: 'Fecha', defaultVisible: false },
    { key: 'tecnico', label: 'Técnico', defaultVisible: false },
];

const ESTADO_CONFIG = {
    pendiente: { color: 'bg-yellow-100 text-yellow-700 border-yellow-300', icon: Clock, label: 'Pendiente' },
    asignada: { color: 'bg-indigo-100 text-indigo-700 border-indigo-300', icon: Clock, label: 'Asignada' },
    en_proceso: { color: 'bg-blue-100 text-blue-700 border-blue-300', icon: Wrench, label: 'En Proceso' },
    realizada: { color: 'bg-purple-100 text-purple-700 border-purple-300', icon: CheckCircle, label: 'Realizada' },
    facturada: { color: 'bg-green-100 text-green-700 border-green-300', icon: DollarSign, label: 'Facturada' },
    cancelada: { color: 'bg-red-100 text-red-700 border-red-300', icon: AlertCircle, label: 'Cancelada' },
};

const PRIORIDAD_CONFIG = {
    alta: { color: 'bg-red-500 text-white', label: 'Alta' },
    media: { color: 'bg-yellow-500 text-white', label: 'Media' },
    baja: { color: 'bg-gray-400 text-white', label: 'Baja' },
};

export default function OrdenesListPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { tienePermiso, loading: permisosLoading } = useOrdenesPermisos();
    const [loading, setLoading] = useState(true);
    const [ordenes, setOrdenes] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [tiposServicio, setTiposServicio] = useState([]);
    
    // Filters
    const [estadoFilter, setEstadoFilter] = useState(searchParams.get('estado') || '');
    const [clienteFilter, setClienteFilter] = useState('');
    const [tipoFilter, setTipoFilter] = useState('');
    const [searchText, setSearchText] = useState('');
    
    // Columnas visibles
    const [visibleColumns, setVisibleColumns] = useState(
        ORDEN_COLUMNS.filter(c => c.defaultVisible !== false).map(c => c.key)
    );

    useEffect(() => {
        loadInitialData();
    }, []);

    useEffect(() => {
        loadOrdenes();
    }, [estadoFilter, clienteFilter, tipoFilter]);

    const loadInitialData = async () => {
        try {
            const [clientesRes, tiposRes] = await Promise.all([
                ordenesClientesAPI.getAll(),
                ordenesCatalogosAPI.getTiposServicio(),
            ]);
            setClientes(clientesRes.data);
            setTiposServicio(tiposRes.data);
        } catch (err) {
            console.error('Error loading initial data:', err);
        }
    };

    const loadOrdenes = async () => {
        try {
            setLoading(true);
            const params = {};
            if (estadoFilter) params.estado = estadoFilter;
            if (clienteFilter) params.cliente_id = clienteFilter;
            if (tipoFilter) params.tipo_servicio = tipoFilter;
            
            const res = await ordenesServicioAPI.getAll(params);
            setOrdenes(res.data);
        } catch (err) {
            console.error('Error loading ordenes:', err);
        } finally {
            setLoading(false);
        }
    };

    const clearFilters = () => {
        setEstadoFilter('');
        setClienteFilter('');
        setTipoFilter('');
        setSearchText('');
        setSearchParams({});
    };

    const hasActiveFilters = estadoFilter || clienteFilter || tipoFilter;

    const filteredOrdenes = ordenes.filter(orden => {
        if (!searchText) return true;
        const searchLower = searchText.toLowerCase();
        return (
            orden.numero_orden?.toLowerCase().includes(searchLower) ||
            orden.cliente?.razon_social?.toLowerCase().includes(searchLower) ||
            orden.vehiculo?.patente?.toLowerCase().includes(searchLower)
        );
    });

    if (permisosLoading) {
        return (
            <div className="space-y-6 p-6">
                <Skeleton className="h-10 w-64" />
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    return (
        <div className="space-y-6 p-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/ordenes/dashboard')} data-testid="back-btn">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <ClipboardList className="h-6 w-6" />
                            Órdenes de Servicio
                        </h1>
                        <p className="text-muted-foreground">Listado de todas las órdenes</p>
                    </div>
                </div>
                {tienePermiso('crear_ordenes') && (
                    <Button onClick={() => navigate('/ordenes/nueva')} data-testid="new-orden-btn">
                        <Plus className="h-4 w-4 mr-2" />
                        Nueva Orden
                    </Button>
                )}
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-4">
                <div className="relative flex-1 min-w-[200px] max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Buscar por N° orden, cliente, patente..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        className="pl-10"
                    />
                </div>
                
                <Select value={estadoFilter || "_all"} onValueChange={(v) => setEstadoFilter(v === "_all" ? "" : v)}>
                    <SelectTrigger className="w-[150px]">
                        <SelectValue placeholder="Estado" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="_all">Todos</SelectItem>
                        {Object.entries(ESTADO_CONFIG).map(([key, config]) => (
                            <SelectItem key={key} value={key}>{config.label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                <Select value={clienteFilter || "_all"} onValueChange={(v) => setClienteFilter(v === "_all" ? "" : v)}>
                    <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Cliente" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="_all">Todos</SelectItem>
                        {clientes.map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.razon_social}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                <Select value={tipoFilter || "_all"} onValueChange={(v) => setTipoFilter(v === "_all" ? "" : v)}>
                    <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Tipo Servicio" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="_all">Todos</SelectItem>
                        {tiposServicio.map(t => (
                            <SelectItem key={t.codigo} value={t.codigo}>{t.nombre}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                {hasActiveFilters && (
                    <Button variant="ghost" size="sm" onClick={clearFilters}>
                        <X className="h-4 w-4 mr-1" />
                        Limpiar
                    </Button>
                )}
                
                <ColumnSelector
                    columns={ORDEN_COLUMNS}
                    visibleColumns={visibleColumns}
                    onVisibilityChange={setVisibleColumns}
                    storageKey="ordenes-columns"
                />
            </div>

            {/* Table */}
            <Card className="border-2">
                <CardContent className="p-0 overflow-x-auto">
                    {loading ? (
                        <div className="p-8">
                            <Skeleton className="h-64 w-full" />
                        </div>
                    ) : filteredOrdenes.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                            <ClipboardList className="h-12 w-12 mx-auto mb-3 opacity-50" />
                            <p>No hay órdenes que coincidan con los filtros</p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    {visibleColumns.includes('numero') && <TableHead>N° Orden</TableHead>}
                                    {visibleColumns.includes('cliente') && <TableHead>Cliente</TableHead>}
                                    {visibleColumns.includes('rut') && <TableHead>RUT</TableHead>}
                                    {visibleColumns.includes('vehiculo') && <TableHead>Vehículo</TableHead>}
                                    {visibleColumns.includes('tipo') && <TableHead>Tipo</TableHead>}
                                    {visibleColumns.includes('estado') && <TableHead>Estado</TableHead>}
                                    {visibleColumns.includes('prioridad') && <TableHead>Prioridad</TableHead>}
                                    {visibleColumns.includes('fecha') && <TableHead>Fecha</TableHead>}
                                    {visibleColumns.includes('tecnico') && <TableHead>Técnico</TableHead>}
                                    <TableHead className="text-right">Acciones</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredOrdenes.map((orden) => {
                                    const estadoConfig = ESTADO_CONFIG[orden.estado] || ESTADO_CONFIG.pendiente;
                                    const prioridadConfig = PRIORIDAD_CONFIG[orden.prioridad] || PRIORIDAD_CONFIG.media;
                                    const Icon = estadoConfig.icon;
                                    
                                    return (
                                        <TableRow 
                                            key={orden.id} 
                                            className="cursor-pointer hover:bg-muted/50"
                                            onClick={() => navigate(`/ordenes/ordenes/${orden.id}`)}
                                            data-testid={`orden-${orden.id}`}
                                        >
                                            {visibleColumns.includes('numero') && (
                                                <TableCell className="font-mono font-bold">{orden.numero_orden}</TableCell>
                                            )}
                                            {visibleColumns.includes('cliente') && (
                                                <TableCell className="font-medium">{orden.cliente?.razon_social || '-'}</TableCell>
                                            )}
                                            {visibleColumns.includes('rut') && (
                                                <TableCell className="font-mono text-xs">{orden.cliente?.rut || '-'}</TableCell>
                                            )}
                                            {visibleColumns.includes('vehiculo') && (
                                                <TableCell>{orden.vehiculo?.patente || '-'}</TableCell>
                                            )}
                                            {visibleColumns.includes('tipo') && (
                                                <TableCell className="text-sm">
                                                    {tiposServicio.find(t => t.codigo === orden.tipo_servicio)?.nombre || orden.tipo_servicio}
                                                </TableCell>
                                            )}
                                            {visibleColumns.includes('estado') && (
                                                <TableCell>
                                                    <Badge className={estadoConfig.color}>
                                                        <Icon className="h-3 w-3 mr-1" />
                                                        {estadoConfig.label}
                                                    </Badge>
                                                </TableCell>
                                            )}
                                            {visibleColumns.includes('prioridad') && (
                                                <TableCell>
                                                    <Badge className={prioridadConfig.color}>
                                                        {prioridadConfig.label}
                                                    </Badge>
                                                </TableCell>
                                            )}
                                            {visibleColumns.includes('fecha') && (
                                                <TableCell>{orden.created_at?.split('T')[0] || '-'}</TableCell>
                                            )}
                                            {visibleColumns.includes('tecnico') && (
                                                <TableCell>{orden.tecnico?.nombre || '-'}</TableCell>
                                            )}
                                            <TableCell>
                                                <Button 
                                                    variant="ghost" 
                                                    size="sm"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/ordenes/ordenes/${orden.id}`);
                                                    }}
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Summary */}
            <div className="text-sm text-muted-foreground text-center">
                Mostrando {filteredOrdenes.length} de {ordenes.length} órdenes
            </div>
        </div>
    );
}
